import {
  Bar,
  BarChart as RBarChart,
  CartesianGrid,
  Cell,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import type { ReactNode } from 'react';
import { palette } from './chartTheme';
import { ChartTooltip } from './ChartTooltip';

interface BarChartProps<T extends object> {
  data: T[];
  xKey: keyof T & string;
  yKey: keyof T & string;
  height?: number;
  horizontal?: boolean;
  formatValue?: (v: number) => ReactNode;
  /** Colors per row (e.g. for status pills). */
  colorFor?: (row: T, i: number) => string;
}

export function BarChart<T extends object>({
  data,
  xKey,
  yKey,
  height = 260,
  horizontal,
  formatValue,
  colorFor,
}: BarChartProps<T>) {
  return (
    <div style={{ width: '100%', height }}>
      <ResponsiveContainer>
        <RBarChart
          data={data}
          layout={horizontal ? 'vertical' : 'horizontal'}
          margin={{ top: 8, right: 12, left: horizontal ? 8 : 0, bottom: 0 }}
        >
          <CartesianGrid strokeDasharray="2 4" stroke={palette.grid()} vertical={!horizontal} horizontal={horizontal} />
          {horizontal ? (
            <>
              <XAxis type="number" stroke={palette.axis()} tickLine={false} axisLine={false} tick={{ fontSize: 11 }} />
              <YAxis dataKey={xKey} type="category" stroke={palette.axis()} tickLine={false} axisLine={false} width={120} tick={{ fontSize: 11 }} />
            </>
          ) : (
            <>
              <XAxis dataKey={xKey} stroke={palette.axis()} tickLine={false} axisLine={false} tick={{ fontSize: 11 }} />
              <YAxis stroke={palette.axis()} tickLine={false} axisLine={false} width={48} tick={{ fontSize: 11 }} />
            </>
          )}
          <Tooltip
            cursor={{ fill: palette.border(), opacity: 0.4 }}
            content={(p) => <ChartTooltip {...p} formatValue={formatValue} />}
          />
          <Bar dataKey={yKey} radius={[4, 4, 0, 0]} isAnimationActive={false}>
            {data.map((row, i) => (
              <Cell key={i} fill={colorFor ? colorFor(row, i) : palette.primary()} />
            ))}
          </Bar>
        </RBarChart>
      </ResponsiveContainer>
    </div>
  );
}
